import { Dropdown } from 'antd';
import { Boxes, Check, ChevronDown } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@features/auth/store';

/**
 * REQ-45 / K-3 — Topbar proje (site) switcher.
 *
 * `availableProjects` login/refresh yanıtından gelir. Tek proje (veya hiç) varsa
 * gizlenir (tek-tenant kurulumda gürültü yapmaz). Seçim değişince:
 *   1. `setActiveProject` → sonraki her isteğe `X-Project-Id` (http.ts).
 *   2. `queryClient.resetQueries()` (global) → önceki projenin cache'i SİLİNİR (yalnız
 *      stale işaretlenmez) ve aktif sorgular yükleme durumuna döner; böylece proje
 *      değişiminde eski projenin verisi bir an bile görünmez (key izolasyon tercihi (a)).
 */
export function ProjectSwitcher() {
  const { t } = useTranslation('projects');
  const qc = useQueryClient();
  const availableProjects = useAuthStore((s) => s.availableProjects);
  const activeProjectId = useAuthStore((s) => s.activeProjectId);
  const setActiveProject = useAuthStore((s) => s.setActiveProject);

  // Tek proje (veya hiç) → switcher gizli.
  if (availableProjects.length <= 1) return null;

  const active =
    availableProjects.find((p) => p.id === activeProjectId) ?? availableProjects[0];
  if (!active) return null;

  const handleSelect = (id: string) => {
    if (id === activeProjectId) return;
    const next = availableProjects.find((p) => p.id === id);
    setActiveProject(id);
    void qc.resetQueries();
    if (next) toast.success(t('switcher.switched', { name: next.name }));
  };

  const items = availableProjects.map((p) => ({
    key: p.id,
    label: (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, minWidth: 140 }}>
        {p.id === active.id ? (
          <Check size={14} color="var(--ds-brand)" />
        ) : (
          <span style={{ width: 14 }} aria-hidden="true" />
        )}
        {p.name}
      </span>
    ),
  }));

  return (
    <Dropdown
      menu={{ items, onClick: ({ key }) => handleSelect(key), selectedKeys: [active.id] }}
      placement="bottomLeft"
      trigger={['click']}
    >
      <button
        type="button"
        className="ds-project-switcher"
        aria-label={t('switcher.ariaLabel')}
        data-testid="project-switcher"
      >
        <Boxes size={15} strokeWidth={1.8} />
        <span className="ds-project-switcher__name">{active.name}</span>
        <ChevronDown size={13} strokeWidth={1.8} />
      </button>
    </Dropdown>
  );
}
